// Single shared "when should the Portal refetch" signal for every LIVE
// panel (useNetwork: target/observers/metrics/events; useCarrier;
// useHunters) — see docs/PORTAL_LIVE_REFRESH.md for the full audit this
// replaces. Before this module, each of those three hooks opened its own
// setInterval poll independently (useNetwork ALSO opened its own
// EventSource), meaning up to 3 uncoordinated 5s timers running at once
// plus one SSE connection none of the others could see.
//
// GET /api/v1/stream is a single global feed — it ignores any query
// string server-side (see main.py::portal_stream, StreamingResponse over
// hub.stream() with no filtering) — so ONE connection already covers every
// panel for every target; each subscriber still does its own per-target
// fetch when notified, this module only decides WHEN, never WHAT.
const listeners = new Set<() => void>();
let source: EventSource | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let refCount = 0;
let healthy = false;

const POLL_MS = 5000;
// Every SSE event name the backend actually publishes via hub.publish (see
// main.py) that could mean any panel's data changed. "reset"/"ready" are
// kept even though nothing currently publishes them — cheap to listen for,
// costly to silently miss if that ever changes.
const EVENT_NAMES = [
  "ready",
  "state",
  "target",
  "observers",
  "metrics",
  "event",
  "target.state",
  "target.updated",
  "detection",
  "recovery",
  "reset",
  "heartbeat",
  "carrier.position",
  "round.started",
  "round.closed",
  "round.reset",
] as const;

function notifyAll() {
  // Pause work while the tab/window is hidden (P1) — visibilitychange's
  // own listener below calls this again once visible, so nothing is lost,
  // just deferred until someone can actually see it.
  if (document.hidden) return;
  for (const l of listeners) l();
}

function onVisibility() {
  if (!document.hidden) notifyAll();
}

function start(base: string) {
  source = new EventSource(`${base}/api/v1/stream`);
  source.onopen = () => {
    healthy = true;
    notifyAll();
  };
  source.onerror = () => {
    healthy = false;
  };
  source.onmessage = notifyAll;
  for (const name of EVENT_NAMES) source.addEventListener(name, notifyAll);
  timer = setInterval(notifyAll, POLL_MS);
  document.addEventListener("visibilitychange", onVisibility);
}

function stop() {
  source?.close();
  source = null;
  if (timer) clearInterval(timer);
  timer = null;
  document.removeEventListener("visibilitychange", onVisibility);
  healthy = false;
}

// Subscribe to live-refresh signals. Returns an unsubscribe function.
// The underlying EventSource/poll timer is reference-counted: the first
// subscriber starts it, the last one to unsubscribe tears it down — so
// navigating away from the Portal (e.g. to /control or /participa) doesn't
// leave a connection open, but multiple panels on the same page share one.
export function subscribeLive(base: string, cb: () => void): () => void {
  if (refCount === 0) start(base);
  refCount++;
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
    refCount--;
    if (refCount <= 0) {
      refCount = 0;
      stop();
    }
  };
}

export function isLiveHealthy(): boolean {
  return healthy;
}
